import { motion } from 'motion/react';
import { currencyGlyph } from '@/lib/money/index.js';
import { useMoneyCurrency } from '@/components/money/MoneyProvider.js';
import { useTranslation } from 'react-i18next';
import { useMoney } from '../../../lib/money/useMoney.js';
import { minorToPlain } from '../state/bet.js';
import { Icon } from '../../../components/ui/icon.js';

const BP = 10000n;

/**
 * Pull the keno paytable matrix out of the (untyped) presenterConfig. The server sends
 * `matrix: [{ picksCount, payouts: number[] }]` covering every pick count 1–10 for the
 * resolved risk, so the bar never needs to re-derive the RTP math client-side.
 * Returns rows indexed by picksCount; null when the config predates the matrix.
 */
export function kenoMatrixFrom(presenterConfig: unknown): Map<number, number[]> | null {
  const raw =
    presenterConfig != null && typeof presenterConfig === 'object' && 'matrix' in presenterConfig
      ? (presenterConfig as { matrix?: unknown }).matrix
      : undefined;
  if (!Array.isArray(raw)) return null;
  const rows = new Map<number, number[]>();
  for (const row of raw) {
    if (
      row != null &&
      typeof row === 'object' &&
      typeof (row as { picksCount?: unknown }).picksCount === 'number' &&
      Array.isArray((row as { payouts?: unknown }).payouts)
    ) {
      const payouts = (row as { payouts: unknown[] }).payouts;
      if (payouts.every((p) => typeof p === 'number')) {
        rows.set((row as { picksCount: number }).picksCount, payouts as number[]);
      }
    }
  }
  return rows.size > 0 ? rows : null;
}

function formatMult(bp: number): string {
  const x = bp / 10000;
  return x >= 100 ? x.toFixed(0) : x.toFixed(2);
}

interface Props {
  /** Multiplier (bp) per hit count for the current picks count; index = hits. */
  payouts: number[];
  bet: bigint;
  /** Live hit count from the grid (drives the moving highlight). */
  hitCount: number;
  /** A round is revealing or settled — light up the tile for the current hit count. */
  highlight: boolean;
  /** Round settled — paint the active tile as win/lose instead of neutral focus. */
  settled: boolean;
  currency?: string;
}

/**
 * Always-visible paytable strip under the keno board: one tile per possible hit count
 * showing the multiplier and what the CURRENT bet would pay. Non-paying tiers render
 * dimmed so "how many do I need to hit?" is answered at a glance, and the active tier
 * follows the reveal in real time.
 */
export function KenoPayoutBar({ payouts, bet, hitCount, highlight, settled, currency }: Props) {
  const { t } = useTranslation('originals');
  const { multiplier } = useMoney(currency);
  const fallbackCurrency = useMoneyCurrency();
  const mult = multiplier();
  if (payouts.length <= 1) return null;

  const minHits = payouts.findIndex((bp) => bp > 0);

  return (
    <div className="flex flex-col gap-2" data-keno-paytable>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]">
        <span className="font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('keno.paytable')}
        </span>
        {minHits > 0 && (
          <span className="font-semibold text-[var(--text-muted)]">
            {t('keno.minHits', { count: minHits })}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {payouts.map((bp, hits) => {
          const pays = bp > 0;
          const active = highlight && hits === hitCount;
          const won = active && settled && pays;
          const payout = (bet * BigInt(bp)) / BP;
          return (
            <motion.div
              key={hits}
              data-paytier={hits}
              data-pays={pays ? 'true' : 'false'}
              data-tier-active={active ? 'true' : 'false'}
              initial={false}
              animate={active ? { scale: [1, 1.1, 1] } : { scale: 1 }}
              transition={{ duration: 0.28, ease: [0.34, 1.56, 0.64, 1] }}
              className={[
                'flex min-w-[46px] flex-1 flex-col items-center gap-0.5 rounded-lg border px-1 py-1.5 transition-colors',
                won
                  ? 'border-[var(--success,#3ddc97)] bg-[var(--success,#3ddc97)]/15 shadow-[0_0_12px_var(--success,#3ddc97)]'
                  : active
                    ? 'border-[var(--primary)] bg-[var(--primary)]/15'
                    : pays
                      ? 'border-[var(--border)] bg-[var(--bg-elevated)]'
                      : 'border-[var(--border)] bg-[var(--bg-elevated)] opacity-45',
              ].join(' ')}
            >
              <span className="flex items-center gap-0.5 text-[10px] font-bold tabular-nums text-[var(--text-secondary)]">
                <Icon
                  name="check"
                  size={10}
                  className={pays ? 'text-[var(--success,#3ddc97)]' : 'text-[var(--text-muted)]'}
                />
                {hits}
              </span>
              <span
                className={[
                  'text-[12px] font-extrabold tabular-nums',
                  won
                    ? 'text-[var(--success,#3ddc97)]'
                    : pays
                      ? 'text-[var(--primary)]'
                      : 'text-[var(--text-muted)]',
                ].join(' ')}
              >
                ×{formatMult(bp)}
              </span>
              <span className="text-[9.5px] tabular-nums text-[var(--text-muted)]">
                {pays ? `${currencyGlyph(currency ?? fallbackCurrency)}${minorToPlain(payout, mult)}` : '—'}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
